import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Paths } from "src/paths";

/*
    Deep link for the record an item has drilled into:
    /engagements/:engagementId/:tab/:itemId.

    Items that open one record at a time keep that selection in the URL rather
    than in local state, so it can be linked, bookmarked, and walked with the
    browser's back and forward buttons.

    Some hosts (e.g. the legacy EngagementModal, opened from Traces) render the
    item outside that route entirely — there is no :engagementId/:tab to link
    into. When those params are absent, fall back to plain component state
    instead of navigating to a nonsensical URL.
*/
export function useItemRoute(): [number | null, (id: number | null) => void] {
  const { engagementId, tab, itemId } = useParams<{
    engagementId: string;
    tab: string;
    itemId: string;
  }>();
  const navigate = useNavigate();
  const [localId, setLocalId] = useState<number | null>(null);

  const hasRouteParams = engagementId != null && tab != null;

  const parsed = itemId ? Number(itemId) : NaN;
  const routeSelectedId = Number.isFinite(parsed) ? parsed : null;

  const select = useCallback(
    (id: number | null) => {
      if (!hasRouteParams) {
        setLocalId(id);
        return;
      }
      const base = `${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${engagementId}/${tab}`;
      navigate(id == null ? base : `${base}/${id}`);
    },
    [hasRouteParams, engagementId, tab, navigate],
  );

  return [hasRouteParams ? routeSelectedId : localId, select];
}

/*
    One level deeper: the sub-view within an opened item, e.g. the Data File
    population detail's Members / Data Spec / Field Map / Files tabs at
    /engagements/:engagementId/:tab/:itemId/:subId. Same fallback rule as
    useItemRoute for hosts rendered outside the engagement route.

    The caller owns validating the returned segment against its known keys —
    a stale or mistyped URL should land on the caller's default, not crash.
*/
export function useSubItemRoute(): [string | null, (sub: string | null) => void] {
  const { engagementId, tab, itemId, subId } = useParams<{
    engagementId: string;
    tab: string;
    itemId: string;
    subId: string;
  }>();
  const navigate = useNavigate();
  const [localSub, setLocalSub] = useState<string | null>(null);

  const hasRouteParams = engagementId != null && tab != null && itemId != null;

  const select = useCallback(
    (sub: string | null) => {
      if (!hasRouteParams) {
        setLocalSub(sub);
        return;
      }
      const base = `${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${engagementId}/${tab}/${itemId}`;
      navigate(sub == null ? base : `${base}/${sub}`);
    },
    [hasRouteParams, engagementId, tab, itemId, navigate],
  );

  return [hasRouteParams ? (subId ?? null) : localSub, select];
}
